import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import ElevatedButton from '../components/Layout/ElevatedButton';
import CustomModal from '../components/Layout/Modal';
import CourseDetailsModal from '../components/Courses/CourseDetailsModal';
import CourseForm from '../components/Courses/CourseForm';
import AssignmentForm from '../components/Assignments/AssignmentForm';
import { FaArrowLeft, FaFile, FaStickyNote, FaCheckSquare, FaEdit, FaTrash, FaPlus, FaDownload, FaEye, FaChevronDown, FaUpload, FaCheck, FaUndo } from 'react-icons/fa';
import { FaBarsProgress } from 'react-icons/fa6';
import { API_ENDPOINTS } from '../api/config';
import { parseGrade, formatGrade } from '../utils/gpa';

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  background: ${props => props.theme.background};
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.background};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;

  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
`;

const GradeInput = styled.input`
  width: 100px;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  margin-top: 0.25rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
`;

const ErrorText = styled.div`
  color: ${props => props.theme.error};
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.25rem;
`;

const AssignmentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const AssignmentTitle = styled.h1`
  margin: 0 0 0.5rem 0;
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const AssignmentMeta = styled.div`
  color: ${props => props.theme.textSecondary};
  margin: 0;
  font-size: 0.9375rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const CourseTitleLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text};
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 4px;
  font-size: inherit;
  padding: 0;
  margin: 0;
  transition: text-decoration 0.2s;

  &:hover {
    text-decoration: underline solid;
  }

  &:focus {
    text-decoration: underline solid;
    outline: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MasterDetailContainer = styled.div`
  flex: 1;
  display: flex;
  gap: 1.5rem;
  padding: 1rem 2rem 1rem 0.5rem;
  overflow: hidden;
`;

const MasterPane = styled.div`
  flex: 0 0 32%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  overflow: hidden;
  padding: 0.5rem 0;
`;

const DetailPane = styled.div`
  flex: 0 0 68%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  overflow: hidden;
`;

const PaneHeader = styled.div`
  padding: 1rem 0.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.cardBackground};
`;

const PaneTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
`;

const PaneContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
`;

const AssignmentActions = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
  opacity: 1;
  transition: opacity 0.2s ease;
  z-index: 20;
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#dc3545' : props.theme.cardBackground};
  border: 1px solid ${props => props.danger ? '#dc3545' : props.theme.border};
  border-radius: 4px;
  color: ${props => props.danger ? 'white' : props.theme.text};
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: all 0.2s ease;
  position: relative;
  z-index: 20;
  pointer-events: auto;

  &:hover {
    background: ${props => props.danger ? '#c82333' : props.theme.hoverBackground};
  }
`;

const PaneActionButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  color: ${props => props.theme.buttonPrimary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;

  &:hover {
    background: ${props => props.theme.buttonPrimary};
    color: white;
  }
`;

const AssignmentItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
  cursor: pointer;
  background: ${props => props.selected ? props.theme.buttonPrimary : 'transparent'};
  color: ${props => props.selected ? 'white' : props.theme.text};
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${props => props.selected ? props.theme.buttonPrimary : props.theme.hoverBackground};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FileUploadButton = styled.button`
  background: ${props => props.theme.buttonSecondary || '#6c757d'};
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.theme.buttonSecondaryHover || '#545b62'};
  }
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NoteTimestamp = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.8rem;
  margin-top: 0.5rem;
`;

const TodoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TodoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
`;

const TodoCheckbox = styled.input`
  margin: 0;
`;

const TodoContent = styled.span`
  flex: 1;
  color: ${props => props.status === 'COMPLETED' ? props.theme.textMuted : props.theme.text};
  text-decoration: ${props => props.status === 'COMPLETED' ? 'line-through' : 'none'};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;

  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
`;

const FileIcon = styled(FaFile)`
  color: ${props => props.theme.textSecondary};
`;

const FileName = styled.span`
  flex: 1;
  color: ${props => props.theme.text};
`;

const FileSizeInfo = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
`;

// Accordion components
const AccordionItem = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  overflow: hidden;
`;

const AccordionHeader = styled.button`
  width: 100%;
  padding: 1rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${props => props.theme.text};
  font-size: 0.95rem;
  font-weight: 600;

  &:hover {
    background: ${props => props.theme.hoverBackground};
  }

  &:focus {
    outline: none;
    background: ${props => props.theme.hoverBackground};
  }
`;

const AccordionContent = styled.div`
  padding: 0 1rem 1rem 1rem;
  color: ${props => props.theme.text};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const TodoActions = styled.div`
  display: flex;
  gap: 0.25rem;
  opacity: 1; /* Always visible for todos */
  transition: opacity 0.2s;
`;

const InsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InsightItem = styled.div`
  padding: 1rem;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
`;

const InsightTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
`;

const InsightContent = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.5;
  margin-bottom: 0.5rem;

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;

const InsightTimestamp = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 3rem 1rem;
  text-align: center;
  color: ${props => props.theme.textMuted};
  font-style: italic;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaneSubtitle = styled.p`
  margin: 0;
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
`;

const AssignmentsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.textMuted};
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: ${props => props.theme.text};
  font-size: 1.25rem;
  font-weight: 600;
`;

const EmptyStateDescription = styled.p`
  margin: 0 0 1.5rem 0;
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
  line-height: 1.5;
  max-width: 300px;
`;

const CourseBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  margin-right: 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.color || props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  font-family: 'Consolas', 'Monaco', monospace;
`;

const AssignmentTitleLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text};
  cursor: pointer;
  font-size: inherit;
  font-weight: 600;
  padding: 0;
  margin: 0;
  text-align: left;
  width: 100%;
  transition: color 0.2s;

  &:hover {
    /* No color change on hover */
  }

  &:focus {
    outline: none;
  }
`;

// Accordion components
const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AccordionTitle = styled.span`
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const AccordionIcon = styled.div`
  color: ${props => props.theme.textSecondary};
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

// Notes components
const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NotesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NotesTitle = styled.h4`
  margin: 0;
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
`;

const NotesActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const NoteItem = styled.div`
  padding: 1rem;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const NoteTitle = styled.h5`
  margin: 0;
  color: ${props => props.theme.text};
  font-size: 0.95rem;
  font-weight: 600;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const NoteContent = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.5;
  margin-bottom: 0.5rem;
`;

// Todos components
const TodosContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TodosHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TodosTitle = styled.h4`
  margin: 0;
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
`;

const TodosActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

// File components
const FileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FileActions = styled.div`
  display: flex;
  gap: 0.25rem;
`;

// Status and Grade components
const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'completed':
        return '#28a745';
      case 'in progress':
        return '#ffc107';
      case 'pending':
        return '#6c757d';
      default:
        return '#6c757d';
    }
  }};
  color: white;
`;

const GradeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    const grade = props.grade?.toLowerCase();
    if (grade?.includes('a')) return '#28a745';
    if (grade?.includes('b')) return '#17a2b8';
    if (grade?.includes('c')) return '#ffc107';
    if (grade?.includes('d')) return '#fd7e14';
    if (grade?.includes('f')) return '#dc3545';
    return '#6c757d';
  }};
  color: white;
`;

// Detail components
const DetailSection = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
`;

const DetailValue = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.5;
`;

const GenericConfirmModal = ({ isOpen, onClose, title, message, confirmText = 'Confirm', cancelText = 'Cancel', onConfirm, confirmButtonColor = '#dc3545' }) => {
  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <div>
        <p style={{ color: 'var(--color-text)', marginBottom: '1rem', fontSize: '1rem' }}>
          {message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={onClose}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: confirmButtonColor,
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

const ToggleButton = styled.button`
  background: ${props => props.active ? props.theme.buttonPrimary : props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  color: ${props => props.theme.text};
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? props.theme.buttonPrimary : props.theme.cardBackgroundHover};
  }
`;

const AssignmentDetail = () => {
  const { courseId, assignmentId } = useParams();
  const navigate = useNavigate();
  const { courses, assignments, loading: dataLoading, deleteAssignment, deleteCourse, updateCourse, updateAssignment, settings } = useData();
  const { user } = useAuth();

  // Set up react-modal
  useEffect(() => {
    if (typeof window !== 'undefined' && document.getElementById('root')) {
      Modal.setAppElement('#root');
    }
  }, []); // Run only once on mount

  const [viewMode, setViewMode] = useState('active');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedCourseForManagement, setSelectedCourseForManagement] = useState(null);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const manualSelectionRef = useRef(false);
  const [notes, setNotes] = useState({ uploads: [], writtenNotes: [], todos: [] });
  const [aiInsights, setAiInsights] = useState([]);
  const [isEditAssignmentModalOpen, setIsEditAssignmentModalOpen] = useState(false);
  const [isAddAssignmentModalOpen, setIsAddAssignmentModalOpen] = useState(false);
  const [isDeleteAssignmentModalOpen, setIsDeleteAssignmentModalOpen] = useState(false);
  const [isEditCourseModalOpen, setIsEditCourseModalOpen] = useState(false);
  const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isAddTodoModalOpen, setIsAddTodoModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const fileInputRef = useRef(null);
  
  // Delete confirmation modals
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const [isDeleteTodoModalOpen, setIsDeleteTodoModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleteCourseModalOpen, setIsDeleteCourseModalOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [isCourseDetailsModalOpen, setIsCourseDetailsModalOpen] = useState(false);
  const [selectedCourseForDetails] = useState(null);
  
  // File viewing
  const [viewingFile, setViewingFile] = useState(null);
  
  // Accordion state
  const [expandedSections, setExpandedSections] = useState(new Set(['details']));
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [todoContent, setTodoContent] = useState('');
  const [isEditTodoModalOpen, setIsEditTodoModalOpen] = useState(false);
  const [todoToEdit, setTodoToEdit] = useState(null);
  const [editTodoContent, setEditTodoContent] = useState('');
  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [editNoteTitle, setEditNoteTitle] = useState('');
  const [editNoteContent, setEditNoteContent] = useState('');
  const [editingAssignment, setEditingAssignment] = useState(null);

  // Grade editing
  const [gradeInputs, setGradeInputs] = useState({});
  const [gradeErrors, setGradeErrors] = useState({});
  const [isEditingGrade, setIsEditingGrade] = useState(false);
  const [gradeInput, setGradeInput] = useState('');
  const [editingGradeId, setEditingGradeId] = useState(null);

  const handleGradeClick = () => {
    setGradeInput(selectedAssignment.grade || '');
    setIsEditingGrade(true);
  };

  const handleListGradeClick = (assignmentId, currentGrade) => {
    setEditingGradeId(assignmentId);
    if (gradeInputs[assignmentId] === undefined) {
      setGradeInputs(prev => ({ ...prev, [assignmentId]: currentGrade || '' }));
    }
  };

  const handleListGradeBlur = (assignmentId) => {
    handleUpdateGrade(assignmentId);
    setEditingGradeId(null);
  };

  const handleListGradeKeyDown = (e, assignmentId) => {
    if (e.key === 'Enter') {
      handleListGradeBlur(assignmentId);
    } else if (e.key === 'Escape') {
      setEditingGradeId(null);
    }
  };



  const handleGradeSave = async () => {
    if (!selectedAssignment) return;
    
    try {
      // Optimistic update
      const updatedAssignment = { ...selectedAssignment, grade: gradeInput };
      setSelectedAssignment(updatedAssignment);
      setIsEditingGrade(false);
      
      // Update in backend
      await updateAssignment(selectedAssignment._id, { grade: gradeInput });
      toast.success('Grade updated');
    } catch (error) {
      console.error('Error updating grade:', error);
      toast.error('Failed to update grade');
    }
  };

  const handleGradeKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleGradeSave();
    } else if (e.key === 'Escape') {
      setIsEditingGrade(false);
    }
  };

  const fetchNotes = async (assignmentId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(assignmentId)}/notes`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || { uploads: [], writtenNotes: [], todos: [] });
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchAiInsights = async (courseId, assignmentId) => {
    if (!courseId) return;
    
    try {
      const response = await fetch(API_ENDPOINTS.AI_COURSE_LEARNING_SUMMARY(courseId), {
        credentials: 'include'
      });

      if (response.ok) {
        const summary = await response.json();
        // Filter insights for this specific assignment
        const assignmentInsights = summary.insights?.filter(insight => 
          insight.assignmentId === assignmentId
        ) || [];
        setAiInsights(assignmentInsights);
      }
    } catch (error) {
      console.error('Error fetching AI insights:', error);
    }
  };

  useEffect(() => {
    // Skip if this was a manual selection to avoid conflicts
    if (manualSelectionRef.current) {
      manualSelectionRef.current = false;
      return;
    }

    // Wait for data to load
    if (dataLoading || courses.length === 0) {
      return;
    }

    // If no courseId provided, we're in the main assignments/courses view
    if (!courseId) {
      setSelectedCourse(null);
      setCourseAssignments([]);
      setSelectedAssignment(null);
      setLoading(false);
      return;
    }

    // For course-specific views, also wait for assignments
    if (assignments.length === 0) {
      return;
    }

    // Find the course by courseId
    const course = courses.find(c => c._id === courseId);

    if (!course) {
      setLoading(false);
      return;
    }

    setSelectedCourse(course);

    // Get all assignments for this course and sort by due date
    const assignmentsForCourse = assignments
      .filter(a => {
        const assignmentCourseId = typeof a.course === 'object' && a.course?._id 
          ? a.course._id 
          : a.course;
        return assignmentCourseId === course._id;
      })
      .sort((a, b) => {
        // Sort by due date (assignments with no due date go to the end)
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });

    setCourseAssignments(assignmentsForCourse);

    // Determine which assignment to select
    let assignmentToSelect = null;

    if (assignmentId) {
      // If assignmentId is provided in URL, select that assignment
      assignmentToSelect = assignmentsForCourse.find(a => a._id === assignmentId);
    }
    // Note: When no assignmentId is provided, we don't auto-select an assignment
    // This allows the assignment list view to be shown

    if (assignmentToSelect) {
      setSelectedAssignment(assignmentToSelect);
      // Fetch notes for this assignment
      fetchNotes(assignmentToSelect._id);
      // Fetch AI insights for this assignment
      fetchAiInsights(course._id, assignmentToSelect._id);
    }

    setLoading(false);
  }, [courseId, assignmentId, assignments, courses, dataLoading, navigate]);

  const handleAssignmentSelect = useCallback((assignment) => {
    // Set manual selection flag to prevent useEffect interference
    manualSelectionRef.current = true;
    // Immediately update the selected assignment for visual feedback
    setSelectedAssignment(assignment);
    // Fetch notes for the new assignment
    fetchNotes(assignment._id);
    // Navigate to the assignment URL
    navigate(`/assignments/${courseId}/${assignment._id}`, { replace: true });
  }, [navigate, courseId]);

  const handleCourseTitleClick = () => {
    navigate(`/flashcards?course=${courseId}`);
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setIsEditAssignmentModalOpen(true);
  };

  const handleDeleteAssignment = (assignment) => {
    const assignmentObj = typeof assignment === 'string' ? { _id: assignment } : assignment;
    setAssignmentToDelete(assignmentObj);
    setIsDeleteAssignmentModalOpen(true);
  };

  const handleConfirmDeleteAssignment = async () => {
    if (!assignmentToDelete || !assignmentToDelete._id) {
      alert('Invalid assignment selected for deletion');
      return;
    }

    try {
      await deleteAssignment(assignmentToDelete._id);

      if (selectedAssignment && selectedAssignment._id === assignmentToDelete._id) {
        // Navigate back to the course masterview without selecting an assignment
        navigate(`/assignments/${courseId || ''}`, { replace: true });
      }
      setIsDeleteAssignmentModalOpen(false);
      setAssignmentToDelete(null);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment. Please try again.');
    }
  };

  const handleCancelDeleteAssignment = () => {
    setIsDeleteAssignmentModalOpen(false);
    setAssignmentToDelete(null);
  };

  const handleToggleCourseStatus = async (course) => {
    try {
      const newStatus = course.status === 'completed' ? 'active' : 'completed';
      await updateCourse(course._id, { ...course, status: newStatus });
      toast.success(`Course marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating course status:', error);
      toast.error('Failed to update course status');
    }
  };

  const handleStatusChange = async (assignment, newStatus, grade = null) => {
    try {
      if (grade) {
        const validation = validateGrade(grade);
        if (!validation.isValid) {
          setGradeErrors(prev => ({ ...prev, [assignment._id]: validation.error }));
          return;
        }
        newStatus = 'completed';
      }

      await updateAssignment(assignment._id, { status: newStatus, grade });
      setGradeInputs(prev => ({ ...prev, [assignment._id]: '' }));
      setGradeErrors(prev => ({ ...prev, [assignment._id]: '' }));
      toast.success(`Assignment marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating assignment status:', error);
      toast.error('Failed to update assignment status');
    }
  };

  const handleGradeInputChange = (assignmentId, value) => {
    setGradeInputs(prev => ({ ...prev, [assignmentId]: value }));
    const validation = validateGrade(value);
    setGradeErrors(prev => ({ ...prev, [assignmentId]: validation.error }));
  };

  const handleUpdateGrade = async (assignmentId) => {
    const grade = gradeInputs[assignmentId];
    if (grade) {
      const validation = validateGrade(grade);
      if (!validation.isValid) {
        setGradeErrors(prev => ({ ...prev, [assignmentId]: validation.error }));
        return;
      }
      try {
        await updateAssignment(assignmentId, { status: 'completed', grade });
        setGradeInputs(prev => ({ ...prev, [assignmentId]: '' }));
        setGradeErrors(prev => ({ ...prev, [assignmentId]: '' }));
        toast.success('Grade updated');
      } catch (error) {
        console.error('Error updating grade:', error);
        toast.error('Failed to update grade');
      }
    }
  };

  const validateGrade = (gradeStr) => {
    if (!gradeStr || gradeStr.trim() === '') return { isValid: true, error: '' };
    const grade = gradeStr.trim().toUpperCase();
    if (/^(A|A-|B\+|B|B-|C\+|C|C-|D\+|D|D-|F)$/.test(grade)) return { isValid: true, error: '' };
    if (grade.endsWith('%')) {
      const num = parseFloat(grade.slice(0, -1));
      if (isNaN(num) || num < 0 || num > 100) return { isValid: false, error: 'Invalid percentage' };
      return { isValid: true, error: '' };
    }
    if (grade.includes('/')) {
      const [n, d] = grade.split('/').map(parseFloat);
      if (isNaN(n) || isNaN(d) || d <= 0 || n > d) return { isValid: false, error: 'Invalid fraction' };
      return { isValid: true, error: '' };
    }
    return { isValid: false, error: 'Invalid format' };
  };

  const handleConfirmDeleteCourse = async () => {
    if (!courseToDelete) return;

    try {
      await deleteCourse(courseToDelete._id);
      setIsDeleteCourseModalOpen(false);
      setCourseToDelete(null);
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again.');
    }
  };

  const handleCancelDeleteCourse = () => {
    setIsDeleteCourseModalOpen(false);
    setCourseToDelete(null);
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!noteTitle.trim() || !noteContent.trim()) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          writtenNotes: [...(notes.writtenNotes || []), {
            id: Date.now().toString(),
            title: noteTitle,
            content: noteContent,
            createdAt: new Date().toISOString()
          }]
        })
      });

      if (response.ok) {
        setNoteTitle('');
        setNoteContent('');
        setIsAddNoteModalOpen(false);
        // Refresh notes
        if (selectedAssignment) {
          fetchNotes(selectedAssignment._id);
        }
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleSubmitTodo = async (e) => {
    e.preventDefault();
    if (!todoContent.trim()) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          todos: [...(notes.todos || []), {
            id: Date.now().toString(),
            content: todoContent,
            completed: false,
            createdAt: new Date().toISOString()
          }]
        })
      });

      if (response.ok) {
        setTodoContent('');
        setIsAddTodoModalOpen(false);
        // Refresh notes
        if (selectedAssignment) {
          fetchNotes(selectedAssignment._id);
        }
      }
    } catch (error) {
      console.error('Error adding todo:', error);
    }
  };

const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Strict premium check to prevent bypassing
    const isExpired = user?.subscriptionEndDate ? new Date() >= new Date(user.subscriptionEndDate) : false;
    const isPremiumUser = ((user?.subscription && user?.subscription !== 'free' && !isExpired && user?.subscriptionStatus !== 'incomplete_expired' && user?.subscriptionStatus !== 'canceled') || (user?.aiTier && user?.aiTier !== 'free'));

    // Default fallbacks in case the backend network request fails (50MB Premium, 10MB Free)
    let maxFileSize = isPremiumUser ? 50 * 1024 * 1024 : 10 * 1024 * 1024; 
    let planName = isPremiumUser ? 'Premium' : 'Free';

    try {
      const limitResponse = await fetch(API_ENDPOINTS.SUBSCRIPTION_UPLOAD_LIMIT, {
        credentials: 'include'
      });

      if (limitResponse.ok) {
        const limitData = await limitResponse.json();
        if (typeof limitData.limitBytes === 'number' && limitData.limitBytes > 0) {
          // If user is expired but backend limit route is lagging, FORCE the free limit
          if (!isPremiumUser && limitData.limitBytes > 10 * 1024 * 1024) {
             maxFileSize = 10 * 1024 * 1024;
             planName = 'Free';
          } else {
             maxFileSize = limitData.limitBytes;
             planName = limitData.planType === 'ai' ? 'AI' : limitData.planType === 'premium' ? 'Premium' : 'Free';
          }
        }
      }
    } catch (error) {
      console.error('Error fetching live upload limit from Stripe:', error);
    }

    // Calculate TOTAL cumulative size of all files being uploaded in this batch
    let totalUploadSize = 0;
    for (let i = 0; i < files.length; i++) {
      totalUploadSize += files[i].size;
    }

    // Block if the total size exceeds the limit
    if (totalUploadSize > maxFileSize) {
      toast.error(`Total upload size (${(totalUploadSize / (1024*1024)).toFixed(2)}MB) exceeds your ${planName} plan limit of ${maxFileSize / (1024*1024)}MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const allowedExtensions = [
      'pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'csv', 'xls', 'xlsx', 'odt', 
      'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico', 
      'ppt', 'pptx', 'pps', 'ppsx', 'odp', 'key'
    ];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        toast.error(`File type ".${ext}" is not supported. Please upload images, documents, or slideshows.`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    const formData = new FormData();
    formData.append('assignmentId', selectedAssignment._id);
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/uploads`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        if (selectedAssignment) fetchNotes(selectedAssignment._id);
        if (fileInputRef.current) fileInputRef.current.value = '';
        toast.success('Files uploaded successfully');
      } else {
        let errorMessage = 'Unknown error';
        try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
        } catch (e) {
            errorMessage = response.statusText || errorMessage;
        }
        
        if (response.status === 413) {
          toast.error(`File too large. Your limit is ${maxFileSize / (1024 * 1024)}MB.`);
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files. Either the file is too large or the upload failed due to an unknown network error.');
    }
  };

  const handleDeleteFile = (upload) => {
    setItemToDelete(upload);
    setIsDeleteFileModalOpen(true);
  };

  const confirmDeleteFile = async () => {
    if (!itemToDelete) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/uploads/${itemToDelete._id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Refresh notes to show updated files
        if (selectedAssignment) {
          fetchNotes(selectedAssignment._id);
        }
      } else {
        console.error('Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setIsDeleteFileModalOpen(false);
      setItemToDelete(null);
    }
  };

  // Note/Todo CRUD handlers
  const handleDeleteNote = (note) => {
    setItemToDelete(note);
    setIsDeleteNoteModalOpen(true);
  };

  const handleEditNote = (note) => {
    setNoteToEdit(note);
    setEditNoteTitle(note.title);
    setEditNoteContent(note.content);
    setIsEditNoteModalOpen(true);
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    if (!editNoteTitle.trim() || !editNoteContent.trim()) return;

    try {
      const updatedNotes = notes.writtenNotes.map(note =>
        note.id === noteToEdit.id
          ? { ...note, title: editNoteTitle, content: editNoteContent }
          : note
      );

      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          writtenNotes: updatedNotes
        })
      });

      if (response.ok) {
        setNotes(prev => ({ ...prev, writtenNotes: updatedNotes }));
        setIsEditNoteModalOpen(false);
        setNoteToEdit(null);
        setEditNoteTitle('');
        setEditNoteContent('');
      }
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const confirmDeleteNote = async () => {
    if (!itemToDelete) return;

    try {
      const updatedNotes = notes.writtenNotes.filter(note => note.id !== itemToDelete.id);
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          writtenNotes: updatedNotes
        })
      });

      if (response.ok) {
        setNotes(prev => ({ ...prev, writtenNotes: updatedNotes }));
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setIsDeleteNoteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleToggleTodo = async (todoId) => {
    // Optimistically update the UI first
    const updatedTodos = notes.todos.map(todo =>
      todo.id === todoId
        ? { ...todo, completed: !todo.completed, status: todo.completed ? 'INPROGRESS' : 'COMPLETED' }
        : todo
    );
    setNotes(prev => ({ ...prev, todos: updatedTodos }));

    try {
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          todos: updatedTodos
        })
      });

      if (!response.ok) {
        // Revert the optimistic update if API call failed
        setNotes(prev => ({ ...prev, todos: notes.todos }));
        console.error('Failed to update todo status');
      }
    } catch (error) {
      // Revert the optimistic update if API call failed
      setNotes(prev => ({ ...prev, todos: notes.todos }));
      console.error('Error toggling todo:', error);
    }
  };

  const handleEditTodo = (todo) => {
    setTodoToEdit(todo);
    setEditTodoContent(todo.content);
    setIsEditTodoModalOpen(true);
  };

  const handleUpdateTodo = async (e) => {
    e.preventDefault();
    if (!editTodoContent.trim() || !todoToEdit) return;

    try {
      const updatedTodos = notes.todos.map(todo =>
        todo.id === todoToEdit.id
          ? { ...todo, content: editTodoContent }
          : todo
      );

      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          todos: updatedTodos
        })
      });

      if (response.ok) {
        setNotes(prev => ({ ...prev, todos: updatedTodos }));
        setIsEditTodoModalOpen(false);
        setTodoToEdit(null);
        setEditTodoContent('');
      }
    } catch (error) {
      console.error('Error updating todo:', error);
    }
  };

  const handleDeleteTodo = (todo) => {
    setItemToDelete(todo);
    setIsDeleteTodoModalOpen(true);
  };

  const confirmDeleteTodo = async () => {
    if (!itemToDelete) return;

    try {
      const updatedTodos = notes.todos.filter(todo => todo.id !== itemToDelete.id);
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENT_BY_ID(selectedAssignment._id)}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          todos: updatedTodos
        })
      });

      if (response.ok) {
        setNotes(prev => ({ ...prev, todos: updatedTodos }));
      }
    } catch (error) {
      console.error('Error deleting todo:', error);
    } finally {
      setIsDeleteTodoModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleViewFile = async (file) => {
    try {
      // Files are served statically from /uploads/assignments/filename
      const filename = file.diskFilename || file.filename;
      const fileUrl = `/uploads/assignments/${filename}`;

      setViewingFile({ ...file, fileUrl });
    } catch (error) {
      setViewingFile({ ...file, fileUrl: `/uploads/assignments/${file.diskFilename || file.filename}` });
    }
  };

  const handleDownloadFile = (file) => {
    const filename = file.diskFilename || file.filename;
    const fileUrl = `/uploads/assignments/${filename}`;
    
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddAssignment = () => {
    setEditingAssignment(null);
    setIsAddAssignmentModalOpen(true);
  };

  const handleEditCourse = (course = null) => {
    if (course) {
      setEditingCourse(course);
    }
    setModalKey(prev => prev + 1);
    setIsEditCourseModalOpen(true);
  };

  const handleDeleteCourse = (course) => {
    setCourseToDelete(course);
    setModalKey(prev => prev + 1);
    setIsDeleteCourseModalOpen(true);
  };

  const handleFormSubmit = () => {
    setIsEditAssignmentModalOpen(false);
    setIsAddAssignmentModalOpen(false);
    setIsEditCourseModalOpen(false);
    setIsAddCourseModalOpen(false);
    setEditingAssignment(null);
    setEditingCourse(null);
    // Refresh data will happen through DataContext
  };

  const toggleAccordion = (section) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const toggleNote = (noteId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString();
  };

  const handleAddCourseClick = () => {
    const isPremium = user?.subscriptionStatus !== 'canceled' && user?.subscriptionStatus !== 'incomplete_expired' && ((user?.subscription && user?.subscription !== 'free' && user?.subscription !== 'basic') || (user?.aiTier && user?.aiTier !== 'free' && user?.aiTier !== 'basic'));
    
    if (!isPremium && courses && courses.length >= 4) {
      toast.error('Free users can only have up to 4 courses. Please upgrade your plan to add more.');
      return;
    }
    setIsAddCourseModalOpen(true);
  };

  if (dataLoading || loading) {
    // Allow Course Management view to render even while loading
    if (!courseId) {
      // Continue to render the course management view below
    } else {
      return (
        <Container>
          <MainContent>
            <EmptyState>Loading assignment...</EmptyState>
          </MainContent>
        </Container>
      );
    }
  }

  // If no courseId provided, show the main courses view
  if (!courseId) {
    return (
      <>
      <Container>
        <MainContent>
          <Header>
            <HeaderLeft>
              <AssignmentInfo>
                <AssignmentTitle>Course Management</AssignmentTitle>
                <AssignmentMeta>Manage your courses and assignments</AssignmentMeta>
              </AssignmentInfo>
            </HeaderLeft>
            <HeaderActions>
              <div style={{ display: 'flex', gap: '0.25rem', marginRight: '0.5rem' }}>
                <ToggleButton active={viewMode === 'active'} onClick={() => setViewMode('active')}>
                  Active
                </ToggleButton>
                <ToggleButton active={viewMode === 'completed'} onClick={() => setViewMode('completed')}>
                  Completed
                </ToggleButton>
              </div>
            </HeaderActions>
          </Header>

          <MasterDetailContainer>
            <MasterPane>
              <PaneHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <PaneTitle>Courses</PaneTitle>
                  <PaneSubtitle>
                    {courses.filter(c => viewMode === 'active' ? c.status !== 'completed' : c.status === 'completed').length} course
                    {courses.filter(c => viewMode === 'active' ? c.status !== 'completed' : c.status === 'completed').length !== 1 ? 's' : ''}
                  </PaneSubtitle>
                </div>
                <PaneActionButton onClick={handleAddCourseClick} title="Add Course">
                  <FaPlus />
                </PaneActionButton>
              </PaneHeader>
              
              <AssignmentsList>
                {courses.filter(c => viewMode === 'active' ? c.status !== 'completed' : c.status === 'completed').length === 0 ? (
                  <EmptyStateContainer>
                    <EmptyStateIcon>
                      <FaFile />
                    </EmptyStateIcon>
                    <EmptyStateTitle>No {viewMode} courses</EmptyStateTitle>
                    <EmptyStateDescription>
                      {viewMode === 'active' 
                        ? 'Get started by adding your first course.' 
                        : 'No completed courses found.'}
                    </EmptyStateDescription>
                    {viewMode === 'active' && (
                      <ElevatedButton onClick={handleAddCourseClick} variant="primary">
                        <FaPlus /> Add Your First Course
                      </ElevatedButton>
                    )}
                  </EmptyStateContainer>
                ) : (
                  courses
                    .filter(c => viewMode === 'active' ? c.status !== 'completed' : c.status === 'completed')
                    .map(course => {
                    const courseAssignments = assignments.filter(a => {
                      const assignmentCourseId = typeof a.course === 'object' && a.course?._id 
                        ? a.course._id 
                        : a.course;
                      return assignmentCourseId === course._id;
                    });
                    
                    return (
                      <AssignmentItem
                        key={course._id}
                        onClick={(e) => {
                          // Don't select course if clicking on buttons
                          if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
                            setSelectedCourseForManagement(course);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <AssignmentInfo>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <CourseBadge color={course.color}>{course.course_code}</CourseBadge>
                            <CourseTitleLink onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/flashcards?course=${course._id}`);
                            }}>
                              {course.name}
                            </CourseTitleLink>
                          </div>
                          <AssignmentMeta>
                            {courseAssignments.length} assignment{courseAssignments.length !== 1 ? 's' : ''}
                          </AssignmentMeta>
                        </AssignmentInfo>
                        <AssignmentActions>
                          <ActionButton onClick={(e) => { e.stopPropagation(); handleToggleCourseStatus(course); }} title={course.status === 'completed' ? "Mark as Active" : "Mark as Completed"}>
                            {course.status === 'completed' ? <FaUndo /> : <FaCheck />}
                          </ActionButton>
                          <ActionButton onClick={() => handleEditCourse(course)} title="Edit Course">
                            <FaEdit />
                          </ActionButton>
                          <ActionButton onClick={() => handleDeleteCourse(course)} title="Delete Course">
                            <FaTrash />
                          </ActionButton>
                        </AssignmentActions>
                      </AssignmentItem>
                    );
                  })
                )}
              </AssignmentsList>
            </MasterPane>
            
            <DetailPane>
              {selectedCourseForManagement ? (
                <>
                  <PaneHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <PaneTitle>{selectedCourseForManagement.name} ({selectedCourseForManagement.course_code})</PaneTitle>
                      <PaneSubtitle>
                        {assignments.filter(a => {
                          const assignmentCourseId = typeof a.course === 'object' && a.course?._id
                            ? a.course._id
                            : a.course;
                          return assignmentCourseId === selectedCourseForManagement._id;
                        }).length} assignment{assignments.filter(a => {
                          const assignmentCourseId = typeof a.course === 'object' && a.course?._id
                            ? a.course._id
                            : a.course;
                          return assignmentCourseId === selectedCourseForManagement._id;
                        }).length !== 1 ? 's' : ''}
                      </PaneSubtitle>
                    </div>
                    <PaneActionButton onClick={() => setIsAddAssignmentModalOpen(true)} title="Add Assignment">
                      <FaPlus />
                    </PaneActionButton>
                  </PaneHeader>

                  <AssignmentsList>
                    {assignments.filter(a => {
                      const assignmentCourseId = typeof a.course === 'object' && a.course?._id
                        ? a.course._id
                        : a.course;
                      return assignmentCourseId === selectedCourseForManagement._id;
                    }).length === 0 ? (
                      <EmptyStateContainer>
                        <EmptyStateIcon>
                          <FaFile />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No assignments yet</EmptyStateTitle>
                        <EmptyStateDescription>
                          Create your first assignment for this course.
                        </EmptyStateDescription>
                      </EmptyStateContainer>
                    ) : (
                      assignments.filter(a => {
                        const assignmentCourseId = typeof a.course === 'object' && a.course?._id
                          ? a.course._id
                          : a.course;
                        return assignmentCourseId === selectedCourseForManagement._id;
                      }).map(assignment => (
                        <AssignmentItem
                          key={assignment._id}
                          onClick={() => navigate(`/assignments/${selectedCourseForManagement._id}/${assignment._id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <AssignmentInfo>
                            <AssignmentTitleLink>{assignment.title}</AssignmentTitleLink>
                            <AssignmentMeta>
                              Due: {formatDate(assignment.due_date)} • {assignment.status || 'Not Started'}
                            </AssignmentMeta>
                            
                            {(!selectedAssignment || selectedAssignment._id !== assignment._id) && (
                              <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Grade:</span>
                                  {editingGradeId === assignment._id ? (
                                    <GradeInput
                                      autoFocus
                                      type="text"
                                      placeholder="e.g. A-, 95%"
                                      value={gradeInputs[assignment._id] !== undefined ? gradeInputs[assignment._id] : (assignment.grade || '')}
                                      onChange={(e) => handleGradeInputChange(assignment._id, e.target.value)}
                                      onBlur={() => handleListGradeBlur(assignment._id)}
                                      onKeyDown={(e) => handleListGradeKeyDown(e, assignment._id)}
                                      style={{ borderColor: gradeErrors[assignment._id] ? '#f44336' : undefined }}
                                    />
                                  ) : (
                                    <div 
                                      onClick={() => handleListGradeClick(assignment._id, assignment.grade)}
                                      style={{ 
                                        cursor: 'pointer', 
                                        flex: 1, 
                                        padding: '0.25rem 0.5rem', 
                                        border: '1px solid transparent',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}
                                      title="Click to edit grade"
                                    >
                                      {assignment.grade ? (
                                        <GradeBadge grade={(() => {
                                          const percentage = parseGrade(assignment.grade);
                                          return percentage !== null ? formatGrade(percentage) : assignment.grade;
                                        })()}>
                                          {(() => {
                                            const percentage = parseGrade(assignment.grade);
                                            return percentage !== null ? formatGrade(percentage) : assignment.grade;
                                          })()}
                                        </GradeBadge>
                                      ) : (
                                        <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: '0.875rem' }}>Add Grade</span>
                                      )}
                                      <FaEdit style={{ opacity: 0.5, fontSize: '0.875rem' }} />
                                    </div>
                                  )}
                                </div>
                                {gradeErrors[assignment._id] && <ErrorText>{gradeErrors[assignment._id]}</ErrorText>}
                              </div>
                            )}
                          </AssignmentInfo>
                          <AssignmentActions onClick={(e) => e.stopPropagation()}>
                            {assignment.status !== 'completed' && (
                              <ActionButton 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleStatusChange(assignment, 'completed', gradeInputs[assignment._id] !== undefined ? gradeInputs[assignment._id] : assignment.grade); 
                                }} 
                                title="Mark as Completed"
                                disabled={!!gradeErrors[assignment._id]}
                              >
                                <FaCheck />
                              </ActionButton>
                            )}
                            
                            {assignment.status !== 'in-progress' && (
                              <ActionButton 
                                onClick={(e) => { e.stopPropagation(); handleStatusChange(assignment, 'in-progress'); }} 
                                title={assignment.status === 'completed' ? "Undo" : "Mark as In Progress"}
                              >
                                {assignment.status === 'completed' ? <FaUndo /> : <FaBarsProgress />}
                              </ActionButton>
                            )}
                            <ActionButton onClick={() => handleEditAssignment(assignment)} title="Edit Assignment">
                              <FaEdit />
                            </ActionButton>
                            <ActionButton onClick={() => handleDeleteAssignment(assignment._id)} title="Delete Assignment">
                              <FaTrash />
                            </ActionButton>
                          </AssignmentActions>
                        </AssignmentItem>
                      ))
                    )}
                  </AssignmentsList>
                </>
              ) : (
                <>
                  <PaneHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <PaneTitle>Unassigned Assignments</PaneTitle>
                      <PaneSubtitle>
                        {assignments.filter(a => !a.course || a.course === null).length} assignment{assignments.filter(a => !a.course || a.course === null).length !== 1 ? 's' : ''}
                      </PaneSubtitle>
                    </div>
                    <PaneActionButton onClick={() => setIsAddAssignmentModalOpen(true)} title="Add Assignment">
                      <FaPlus />
                    </PaneActionButton>
                  </PaneHeader>
                  <AssignmentsList>
                    {assignments.filter(a => !a.course || a.course === null).length === 0 ? (
                      <EmptyStateContainer>
                        <EmptyStateIcon>
                          <FaFile />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No unassigned assignments</EmptyStateTitle>
                        <EmptyStateDescription>
                          All assignments are linked to courses. Select a course to view its assignments.
                        </EmptyStateDescription>
                      </EmptyStateContainer>
                    ) : (
                      assignments.filter(a => !a.course || a.course === null).map(assignment => (
                        <AssignmentItem
                          key={assignment._id}
                          onClick={() => navigate(`/assignments/none/${assignment._id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <AssignmentInfo>
                            <AssignmentTitleLink>{assignment.title}</AssignmentTitleLink>
                            <AssignmentMeta>
                              Due: {formatDate(assignment.due_date)} • {assignment.status || 'Not Started'}
                            </AssignmentMeta>
                          </AssignmentInfo>
                          <AssignmentActions>
                            <ActionButton onClick={(e) => { e.stopPropagation(); handleEditAssignment(assignment); }} title="Edit Assignment">
                              <FaEdit />
                            </ActionButton>
                            <ActionButton onClick={(e) => { e.stopPropagation(); handleDeleteAssignment(assignment._id); }} title="Delete Assignment">
                              <FaTrash />
                            </ActionButton>
                          </AssignmentActions>
                        </AssignmentItem>
                      ))
                    )}
                  </AssignmentsList>
                </>
              )}
            </DetailPane>
          </MasterDetailContainer>
        </MainContent>
      </Container>

      {/* Modals for Course Management */}
      <CustomModal
        key={`edit-course-${modalKey}`}
        isOpen={isEditCourseModalOpen}
        onClose={() => setIsEditCourseModalOpen(false)}
        title="Edit Course"
      >
        <CourseForm
          course={editingCourse || selectedCourse}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsEditCourseModalOpen(false)}
          isEditing={true}
        />
      </CustomModal>

      <CustomModal
        key={`add-course-${isAddCourseModalOpen}`}
        isOpen={isAddCourseModalOpen}
        onClose={() => setIsAddCourseModalOpen(false)}
        title="Add Course"
      >
        <CourseForm
          onSubmit={handleFormSubmit}
          onCancel={() => setIsAddCourseModalOpen(false)}
          isEditing={false}
        />
      </CustomModal>

      <GenericConfirmModal
        key={`delete-course-${modalKey}`}
        isOpen={isDeleteCourseModalOpen}
        onClose={handleCancelDeleteCourse}
        onConfirm={handleConfirmDeleteCourse}
        title="Delete Course"
        message={`Are you sure you want to delete "${courseToDelete?.name}"? This will also delete all assignments for this course. This action cannot be undone.`}
        confirmText="Delete Course"
        cancelText="Cancel"
        confirmButtonColor="#dc3545"
      />

      {/* Assignment Modals for Course Management View */}
      <CustomModal
        isOpen={isEditAssignmentModalOpen}
        onClose={() => setIsEditAssignmentModalOpen(false)}
        title="Edit Assignment"
      >
        <AssignmentForm
          assignment={editingAssignment}
          courseId={editingAssignment?.course?._id || editingAssignment?.course}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsEditAssignmentModalOpen(false)}
          isEditing={true}
        />
      </CustomModal>

      <CustomModal
        isOpen={isAddAssignmentModalOpen}
        onClose={() => setIsAddAssignmentModalOpen(false)}
        title="Add Assignment"
      >
        <AssignmentForm
          courseId={selectedCourseForManagement?._id}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsAddAssignmentModalOpen(false)}
          isEditing={false}
        />
      </CustomModal>

      <GenericConfirmModal
        isOpen={isDeleteAssignmentModalOpen}
        onClose={handleCancelDeleteAssignment}
        onConfirm={handleConfirmDeleteAssignment}
        title="Delete Assignment"
        message={`Are you sure you want to delete "${assignmentToDelete?.title}"? This will also delete all associated notes, files, and todos. This action cannot be undone.`}
        confirmText="Delete Assignment"
        cancelText="Cancel"
        confirmButtonColor="#dc3545"
      />

      <CourseDetailsModal
        course={selectedCourseForDetails}
        isOpen={isCourseDetailsModalOpen}
        onClose={() => setIsCourseDetailsModalOpen(false)}
      />
      </>
    );
  }

  if (!selectedAssignment) {
    return (
      <Container>
        <MainContent>
          <EmptyState>Assignment not found</EmptyState>
        </MainContent>
      </Container>
    );
  }

  if (!selectedCourse) {
    return (
      <Container>
        <MainContent>
          <EmptyState>Loading course information...</EmptyState>
        </MainContent>
      </Container>
    );
  }

  // Final return for assignment detail view
  return (
    <>
      {(() => {
        if (dataLoading || loading) {
          return (
            <Container>
              <MainContent>
                <EmptyState>Loading assignment...</EmptyState>
              </MainContent>
            </Container>
          );
        }

        // If courseId exists but assignmentId doesn't, show assignment list view
        if (courseId && !assignmentId) {
          if (!selectedCourse) {
            return (
              <Container>
                <MainContent>
                  <EmptyState>Loading course information...</EmptyState>
                </MainContent>
              </Container>
            );
          }

          return (
            <Container>
              <MainContent>
                <Header>
                  <HeaderLeft>
                    <BackButton onClick={() => navigate('/assignments')}>
                      <FaArrowLeft /> Back to Courses
                    </BackButton>
                    <AssignmentInfo>
                      <AssignmentTitle>{selectedCourse.name} ({selectedCourse.course_code})</AssignmentTitle>
                      <AssignmentMeta>{courseAssignments.length} assignment{courseAssignments.length !== 1 ? 's' : ''}</AssignmentMeta>
                    </AssignmentInfo>
                  </HeaderLeft>
                  <HeaderActions>
                    <ElevatedButton onClick={handleAddAssignment} variant="primary">
                      <FaPlus /> Add Assignment
                    </ElevatedButton>
                    <ElevatedButton onClick={handleEditCourse} variant="secondary">
                      <FaEdit /> Edit Course
                    </ElevatedButton>
                  </HeaderActions>
                </Header>

                <MasterDetailContainer>
                  <MasterPane>
                    <PaneHeader>
                      <PaneTitle>Assignments</PaneTitle>
                      <PaneSubtitle>{courseAssignments.length} assignment{courseAssignments.length !== 1 ? 's' : ''}</PaneSubtitle>
                    </PaneHeader>

                    <AssignmentsList>
                      {courseAssignments.length === 0 ? (
                        <EmptyStateContainer>
                          <EmptyStateIcon>
                            <FaFile />
                          </EmptyStateIcon>
                          <EmptyStateTitle>No assignments yet</EmptyStateTitle>
                          <EmptyStateDescription>
                            Create your first assignment for this course.
                          </EmptyStateDescription>
                          <ElevatedButton onClick={handleAddAssignment} variant="primary">
                            <FaPlus /> Add Assignment
                          </ElevatedButton>
                        </EmptyStateContainer>
                      ) : (
                        courseAssignments.map(assignment => {
                          const assignmentCourse = courses.find(c => c._id === assignment.course_id) || selectedCourse;
                          return (
                            <AssignmentItem
                              key={assignment._id}
                              onClick={(e) => {
                                // Don't navigate if clicking on buttons
                                if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
                                  navigate(`/assignments/${courseId}/${assignment._id}`);
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <AssignmentInfo>
                                <AssignmentTitleLink>{assignment.title}</AssignmentTitleLink>
                                <AssignmentMeta>
                                  Due: {formatDate(assignment.due_date)} • {assignmentCourse ? `${assignmentCourse.name} (${assignmentCourse.course_code})` : 'Unknown course'}
                                </AssignmentMeta>
                              </AssignmentInfo>
                              <AssignmentActions>
                                <ActionButton onClick={() => handleEditAssignment(assignment)}>
                                  <FaEdit />
                                </ActionButton>
                                <ActionButton onClick={() => handleDeleteAssignment(assignment._id)}>
                                  <FaTrash />
                                </ActionButton>
                              </AssignmentActions>
                            </AssignmentItem>
                          );
                        })
                      )}
                    </AssignmentsList>
                  </MasterPane>

                  <DetailPane>
                    <EmptyStateContainer>
                      <EmptyStateIcon>
                        <FaFile />
                      </EmptyStateIcon>
                      <EmptyStateTitle>Select an assignment</EmptyStateTitle>
                      <EmptyStateDescription>
                        Choose an assignment from the list to view its details, notes, and files.
                      </EmptyStateDescription>
                    </EmptyStateContainer>
                  </DetailPane>
                </MasterDetailContainer>
              </MainContent>
            </Container>
          );
        }

        if (assignmentId && !selectedAssignment) {
          return (
            <Container>
              <MainContent>
                <EmptyState>Assignment not found</EmptyState>
              </MainContent>
            </Container>
          );
        }

        if (!selectedCourse) {
          return (
            <Container>
              <MainContent>
                <EmptyState>Loading course information...</EmptyState>
              </MainContent>
            </Container>
          );
        }

        return (
          <Container>
            <MainContent>
              <Header>
                <HeaderLeft>
                  <BackButton onClick={() => navigate('/assignments')}>
                    <FaArrowLeft /> Back to Assignments
                  </BackButton>
                  <AssignmentInfo>
                    <AssignmentTitle>{selectedAssignment.title}</AssignmentTitle>
                    <AssignmentMeta>
                      <CourseTitleLink onClick={handleCourseTitleClick}>
                        {selectedCourse ? `${selectedCourse.name} (${selectedCourse.course_code})` : 'Loading course...'}
                      </CourseTitleLink> • Due: {formatDate(selectedAssignment.due_date)}
                    </AssignmentMeta>
                  </AssignmentInfo>
                </HeaderLeft>
                <HeaderActions>
                </HeaderActions>
              </Header>

              <MasterDetailContainer>
                <MasterPane>
                  <PaneHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <PaneTitle>Assignments</PaneTitle>
                      <PaneSubtitle>{courseAssignments.length} assignment{courseAssignments.length !== 1 ? 's' : ''}</PaneSubtitle>
                    </div>
                    <PaneActionButton onClick={handleAddAssignment} title="Add Assignment">
                      <FaPlus />
                    </PaneActionButton>
                  </PaneHeader>

                  <AssignmentsList>
                    {courseAssignments.length === 0 ? (
                      <EmptyStateContainer>
                        <EmptyStateIcon>
                          <FaFile />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No assignments yet</EmptyStateTitle>
                        <EmptyStateDescription>
                          Get started by adding your first assignment for this course.
                        </EmptyStateDescription>
                        <ElevatedButton onClick={handleAddAssignment} variant="primary">
                          <FaPlus /> Add Assignment
                        </ElevatedButton>
                      </EmptyStateContainer>
                    ) : (
                      courseAssignments.map(assignment => (
                        <AssignmentItem
                          key={assignment._id}
                          selected={selectedAssignment._id === assignment._id}
                          onClick={() => handleAssignmentSelect(assignment)}
                        >
                          <AssignmentInfo>
                            <AssignmentTitleLink>{assignment.title}</AssignmentTitleLink>
                            <AssignmentMeta>
                              Due: {formatDate(assignment.due_date)} • {assignment.status}
                            </AssignmentMeta>
                          </AssignmentInfo>
                        </AssignmentItem>
                      ))
                    )}
                  </AssignmentsList>
                </MasterPane>

                <DetailPane>
                  <PaneContent>
                    <AccordionContainer>
                      <AccordionItem>
                        <AccordionHeader onClick={() => toggleAccordion('details')}>
                          <AccordionTitle>Assignment Details</AccordionTitle>
                          <AccordionIcon $isOpen={expandedSections.has('details')}>
                            <FaChevronDown />
                          </AccordionIcon>
                        </AccordionHeader>
                        {expandedSections.has('details') && (
                          <AccordionContent>
                            <DetailSection>
                              <DetailLabel>Title</DetailLabel>
                              <DetailValue>{selectedAssignment.title}</DetailValue>
                            </DetailSection>

                            <DetailSection>
                              <DetailLabel>Description</DetailLabel>
                              <DetailValue>
                                {selectedAssignment.description || 'No description provided'}
                              </DetailValue>
                            </DetailSection>

                            <DetailSection>
                              <DetailLabel>Due Date</DetailLabel>
                              <DetailValue>{formatDate(selectedAssignment.due_date)}</DetailValue>
                            </DetailSection>

                            <DetailSection>
                              <DetailLabel>Status</DetailLabel>
                              <DetailValue>
                                <StatusBadge status={selectedAssignment.status}>
                                  {selectedAssignment.status}
                                </StatusBadge>
                              </DetailValue>
                            </DetailSection>

                            <DetailSection>
                              <DetailLabel>Grade</DetailLabel>
                              <DetailValue>
                                {isEditingGrade ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                      type="text"
                                      value={gradeInput}
                                      onChange={(e) => setGradeInput(e.target.value)}
                                      onBlur={handleGradeSave}
                                      onKeyDown={handleGradeKeyDown}
                                      autoFocus
                                      placeholder="e.g. 95, A, 50/50"
                                      style={{
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        border: '1px solid #444',
                                        background: 'rgba(0,0,0,0.2)',
                                        color: 'inherit',
                                        width: '120px',
                                        fontSize: '0.875rem'
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div 
                                    onClick={handleGradeClick} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                    title="Click to edit grade"
                                  >
                                    {selectedAssignment.grade ? (
                                      <GradeBadge grade={(() => {
                                        const percentage = parseGrade(selectedAssignment.grade, settings);
                                        return percentage !== null ? formatGrade(percentage, settings) : 'F';
                                      })()}>
                                        {selectedAssignment.grade}
                                        {(() => {
                                          const percentage = parseGrade(selectedAssignment.grade, settings);
                                          if (percentage !== null) {
                                            const letter = formatGrade(percentage, settings);
                                            // Only show letter if it's different from the input and input isn't just the percentage number
                                            if (letter !== selectedAssignment.grade && !selectedAssignment.grade.includes(letter)) {
                                              return ` (${letter})`;
                                            }
                                          }
                                          return '';
                                        })()}
                                      </GradeBadge>
                                    ) : (
                                      <span style={{ opacity: 0.5, fontStyle: 'italic', fontSize: '0.875rem' }}>Add Grade</span>
                                    )}
                                    <FaEdit size={12} style={{ opacity: 0.5 }} />
                                  </div>
                                )}
                              </DetailValue>
                            </DetailSection>
                          </AccordionContent>
                        )}
                      </AccordionItem>

                      <AccordionItem>
                        <AccordionHeader onClick={() => toggleAccordion('notes')}>
                          <AccordionTitle>
                            Notes & Files ({notes.uploads.length + notes.writtenNotes.length})
                          </AccordionTitle>
                          <AccordionIcon $isOpen={expandedSections.has('notes')}>
                            <FaChevronDown />
                          </AccordionIcon>
                        </AccordionHeader>
                        {expandedSections.has('notes') && (
                          <AccordionContent>
                            <NotesContainer>
                              <NotesHeader>
                                <NotesTitle>Notes & Files</NotesTitle>
                                <NotesActions>
                                  <ElevatedButton onClick={() => setIsAddNoteModalOpen(true)} variant="secondary" size="small">
                                    <FaPlus /> Add Note
                                  </ElevatedButton>
                                  <FileUploadButton onClick={() => fileInputRef.current?.click()}>
                                    <FaUpload /> Upload File
                                  </FileUploadButton>
                                </NotesActions>
                              </NotesHeader>

                              <NotesList>
                                {notes.writtenNotes.map(note => (
                                  <NoteItem key={note._id || note.id}>
                                    <NoteHeader onClick={() => toggleNote(note.id || note._id)} style={{ cursor: 'pointer' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <AccordionIcon $isOpen={expandedNotes.has(note.id || note._id)}>
                                          <FaChevronDown />
                                        </AccordionIcon>
                                        <NoteTitle>{note.title}</NoteTitle>
                                      </div>
                                      <NoteActions onClick={(e) => e.stopPropagation()}>
                                        <ActionButton onClick={() => handleEditNote(note)}>
                                          <FaEdit />
                                        </ActionButton>
                                        <ActionButton onClick={() => handleDeleteNote(note)}>
                                          <FaTrash />
                                        </ActionButton>
                                      </NoteActions>
                                    </NoteHeader>
                                    {expandedNotes.has(note.id || note._id) && (
                                      <>
                                        <NoteContent>{note.content}</NoteContent>
                                        <NoteTimestamp>
                                          {new Date(note.createdAt).toLocaleDateString()} at {new Date(note.createdAt).toLocaleTimeString()}
                                        </NoteTimestamp>
                                      </>
                                    )}
                                  </NoteItem>
                                ))}

                                {notes.uploads.map(file => (
                                  <FileItem key={file._id}>
                                    <FileHeader>
                                      <FileInfo>
                                        <FileIcon>
                                          <FaFile />
                                        </FileIcon>
                                        <FileDetails>
                                          <FileName>{file.filename}</FileName>
                                          <FileSizeInfo>
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                          </FileSizeInfo>
                                        </FileDetails>
                                      </FileInfo>
                                      <FileActions>
                                        <ActionButton onClick={() => handleViewFile(file)}>
                                          <FaEye />
                                        </ActionButton>
                                        <ActionButton onClick={() => handleDownloadFile(file)}>
                                          <FaDownload />
                                        </ActionButton>
                                        <ActionButton onClick={() => handleDeleteFile(file)}>
                                          <FaTrash />
                                        </ActionButton>
                                      </FileActions>
                                    </FileHeader>
                                  </FileItem>
                                ))}

                                {notes.uploads.length === 0 && notes.writtenNotes.length === 0 && (
                                  <EmptyStateContainer>
                                    <EmptyStateIcon>
                                      <FaStickyNote />
                                    </EmptyStateIcon>
                                    <EmptyStateTitle>No notes or files yet</EmptyStateTitle>
                                    <EmptyStateDescription>
                                      Add notes or upload files to keep track of your assignment progress.
                                    </EmptyStateDescription>
                                  </EmptyStateContainer>
                                )}
                              </NotesList>
                            </NotesContainer>
                          </AccordionContent>
                        )}
                      </AccordionItem>

                      <AccordionItem>
                        <AccordionHeader onClick={() => toggleAccordion('todos')}>
                          <AccordionTitle>
                            Todos ({notes.todos.length})
                          </AccordionTitle>
                          <AccordionIcon $isOpen={expandedSections.has('todos')}>
                            <FaChevronDown />
                          </AccordionIcon>
                        </AccordionHeader>
                        {expandedSections.has('todos') && (
                          <AccordionContent>
                            <TodosContainer>
                              <TodosHeader>
                                <TodosTitle>Todos</TodosTitle>
                                <TodosActions>
                                  <ElevatedButton onClick={() => setIsAddTodoModalOpen(true)} variant="secondary" size="small">
                                    <FaPlus /> Add Todo
                                  </ElevatedButton>
                                </TodosActions>
                              </TodosHeader>

                              <TodoList>
                                {notes.todos.map(todo => (
                                  <TodoItem key={todo._id} completed={todo.completed}>
                                    <TodoCheckbox
                                      type="checkbox"
                                      checked={todo.completed}
                                      onChange={() => handleToggleTodo(todo.id)}
                                    />
                                    <TodoContent completed={todo.completed}>
                                      {todo.content}
                                    </TodoContent>
                                    <TodoActions>
                                      <ActionButton onClick={() => handleEditTodo(todo)}>
                                        <FaEdit />
                                      </ActionButton>
                                      <ActionButton onClick={() => handleDeleteTodo(todo)}>
                                        <FaTrash />
                                      </ActionButton>
                                    </TodoActions>
                                  </TodoItem>
                                ))}

                                {notes.todos.length === 0 && (
                                  <EmptyStateContainer>
                                    <EmptyStateIcon>
                                      <FaCheckSquare />
                                    </EmptyStateIcon>
                                    <EmptyStateTitle>No todos yet</EmptyStateTitle>
                                    <EmptyStateDescription>
                                      Break down your assignment into manageable tasks.
                                    </EmptyStateDescription>
                                  </EmptyStateContainer>
                                )}
                              </TodoList>
                            </TodosContainer>
                          </AccordionContent>
                        )}
                      </AccordionItem>

                      {aiInsights.length > 0 && (
                        <AccordionItem>
                          <AccordionHeader onClick={() => toggleAccordion('insights')}>
                            <AccordionTitle>
                              AI Insights ({aiInsights.length})
                            </AccordionTitle>
                            <AccordionIcon $isOpen={expandedSections.has('insights')}>
                              <FaChevronDown />
                            </AccordionIcon>
                          </AccordionHeader>
                          {expandedSections.has('insights') && (
                            <AccordionContent>
                              <InsightsList>
                                {aiInsights.map(insight => (
                                  <InsightItem key={insight._id}>
                                    <InsightTitle>{insight.title}</InsightTitle>
                                    <InsightContent dangerouslySetInnerHTML={{ __html: insight.content }} />
                                    <InsightTimestamp>
                                      {new Date(insight.createdAt).toLocaleDateString()}
                                    </InsightTimestamp>
                                  </InsightItem>
                                ))}
                              </InsightsList>
                            </AccordionContent>
                          )}
                        </AccordionItem>
                      )}
                    </AccordionContainer>
                  </PaneContent>
                </DetailPane>
              </MasterDetailContainer>
            </MainContent>
          </Container>
        );
      })()}

      {/* Modals - Already rendered in course management view above */}
      {courseId && (
      <>
      <CourseDetailsModal
        course={selectedCourseForDetails}
        isOpen={isCourseDetailsModalOpen}
        onClose={() => setIsCourseDetailsModalOpen(false)}
      />

      <CustomModal
        isOpen={isEditAssignmentModalOpen}
        onClose={() => setIsEditAssignmentModalOpen(false)}
        title="Edit Assignment"
      >
        <AssignmentForm
          assignment={editingAssignment}
          courseId={courseId}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsEditAssignmentModalOpen(false)}
          isEditing={true}
        />
      </CustomModal>

      <CustomModal
        isOpen={isAddAssignmentModalOpen}
        onClose={() => setIsAddAssignmentModalOpen(false)}
        title="Add Assignment"
      >
        <AssignmentForm
          courseId={courseId}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsAddAssignmentModalOpen(false)}
          isEditing={false}
        />
      </CustomModal>

      <CustomModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        title="Add Note"
      >
        <div>
          <form onSubmit={handleSubmitNote}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                Note Title
              </label>
              <input
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-background)',
                  color: 'var(--color-text)'
                }}
                placeholder="Enter note title"
                required
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                Note Content
              </label>
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-background)',
                  color: 'var(--color-text)',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
                placeholder="Enter note content"
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setIsAddNoteModalOpen(false)}
                style={{
                  background: 'var(--color-textMuted)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: 'var(--color-buttonPrimary)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Add Note
              </button>
            </div>
          </form>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={isEditNoteModalOpen}
        onClose={() => setIsEditNoteModalOpen(false)}
        title="Edit Note"
      >
        <div>
          <form onSubmit={handleUpdateNote}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                Note Title
              </label>
              <input
                type="text"
                value={editNoteTitle}
                onChange={(e) => setEditNoteTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-background)',
                  color: 'var(--color-text)'
                }}
                placeholder="Enter note title"
                required
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                Note Content
              </label>
              <textarea
                value={editNoteContent}
                onChange={(e) => setEditNoteContent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-background)',
                  color: 'var(--color-text)',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
                placeholder="Enter note content"
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setIsEditNoteModalOpen(false)}
                style={{
                  background: 'var(--color-textMuted)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: 'var(--color-buttonPrimary)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Update Note
              </button>
            </div>
          </form>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={isAddTodoModalOpen}
        onClose={() => setIsAddTodoModalOpen(false)}
        title="Add Todo"
      >
        <div>
          <form onSubmit={handleSubmitTodo}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                Todo Content
              </label>
              <input
                type="text"
                value={todoContent}
                onChange={(e) => setTodoContent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-background)',
                  color: 'var(--color-text)'
                }}
                placeholder="Enter todo item"
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setIsAddTodoModalOpen(false)}
                style={{
                  background: 'var(--color-textMuted)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: 'var(--color-buttonPrimary)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Add Todo
              </button>
            </div>
          </form>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={isEditTodoModalOpen}
        onClose={() => setIsEditTodoModalOpen(false)}
        title="Edit Todo"
      >
        <div>
          <form onSubmit={handleUpdateTodo}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
                Todo Content
              </label>
              <input
                type="text"
                value={editTodoContent}
                onChange={(e) => setEditTodoContent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  background: 'var(--color-background)',
                  color: 'var(--color-text)'
                }}
                placeholder="Enter todo item"
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setIsEditTodoModalOpen(false)}
                style={{
                  background: 'var(--color-textMuted)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: 'var(--color-buttonPrimary)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Update Todo
              </button>
            </div>
          </form>
        </div>
      </CustomModal>

      {/* File Viewer Modal */}
      <CustomModal
        isOpen={viewingFile !== null}
        onClose={() => setViewingFile(null)}
        title={viewingFile?.filename || 'File Viewer'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '200px', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text)' }}>
            <p>Previews are disabled. Use the download button below.</p>
          </div>

          <div style={{ marginTop: '1rem', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ElevatedButton 
              onClick={() => handleDownloadFile(viewingFile)}
              variant="primary"
            >
              <FaDownload style={{ marginRight: '0.5rem' }} /> Download File
            </ElevatedButton>
          </div>
        </div>
      </CustomModal>

      {/* Delete Confirmation Modals */}
      <GenericConfirmModal
        key={`delete-course-${modalKey}`}
        isOpen={isDeleteCourseModalOpen}
        onClose={handleCancelDeleteCourse}
        onConfirm={handleConfirmDeleteCourse}
        title="Delete Course"
        message={`Are you sure you want to delete "${courseToDelete?.name}"? This will also delete all assignments for this course. This action cannot be undone.`}
        confirmText="Delete Course"
        cancelText="Cancel"
        confirmButtonColor="#dc3545"
      />

      <GenericConfirmModal
        isOpen={isDeleteFileModalOpen}
        onClose={() => setIsDeleteFileModalOpen(false)}
        onConfirm={confirmDeleteFile}
        title="Delete File"
        message={`Are you sure you want to delete "${itemToDelete?.filename}"? This action cannot be undone.`}
        confirmText="Delete File"
        cancelText="Cancel"
      />

      <GenericConfirmModal
        isOpen={isDeleteNoteModalOpen}
        onClose={() => setIsDeleteNoteModalOpen(false)}
        onConfirm={confirmDeleteNote}
        title="Delete Note"
        message={`Are you sure you want to delete the note "${itemToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete Note"
        cancelText="Cancel"
      />

      <GenericConfirmModal
        isOpen={isDeleteTodoModalOpen}
        onClose={() => setIsDeleteTodoModalOpen(false)}
        onConfirm={confirmDeleteTodo}
        title="Delete Todo"
        message={`Are you sure you want to delete the todo item "${itemToDelete?.content}"? This action cannot be undone.`}
        confirmText="Delete Todo"
        cancelText="Cancel"
      />

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        multiple
      />
      </>
      )}
    </>
  );
};

export default AssignmentDetail;
